import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import Button from '../common/Button';

const Register = ({ onSwitchToLogin }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    gymId: ''
  });
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [gyms, setGyms] = useState([]);
  const [loadingGyms, setLoadingGyms] = useState(true);
  const [gymSearchQuery, setGymSearchQuery] = useState('');
  const [showGymDropdown, setShowGymDropdown] = useState(false);
  const [selectedGym, setSelectedGym] = useState(null);
  const { register } = useContext(AuthContext);
  const navigate = useNavigate();

  // Fetch gyms on component mount
  useEffect(() => {
    const fetchGyms = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3001'}/api/gyms/public`);
        if (response.ok) {
          const data = await response.json();
          setGyms(data);
        } else {
          console.error('Failed to fetch gyms');
        }
      } catch (error) {
        console.error('Error fetching gyms:', error);
      } finally {
        setLoadingGyms(false);
      }
    };

    fetchGyms();
  }, []);

  // Filter gyms based on search query
  const filteredGyms = gyms.filter(gym => {
    const searchLower = gymSearchQuery.toLowerCase();
    return (
      gym.name.toLowerCase().includes(searchLower) ||
      (gym.city && gym.city.toLowerCase().includes(searchLower)) ||
      (gym.state && gym.state.toLowerCase().includes(searchLower)) ||
      (gym.address_line_1 && gym.address_line_1.toLowerCase().includes(searchLower))
    );
  });

  // Handle gym search input change
  const handleGymSearchChange = (e) => {
    setGymSearchQuery(e.target.value);
    setShowGymDropdown(true);
    if (errors.gymId) {
      setErrors(prev => ({ ...prev, gymId: '' }));
    }
  };

  // Handle gym selection
  const handleGymSelect = (gym) => {
    setSelectedGym(gym);
    setGymSearchQuery(gym.name);
    setFormData(prev => ({ ...prev, gymId: gym.id.toString() }));
    setShowGymDropdown(false);
    if (errors.gymId) {
      setErrors(prev => ({ ...prev, gymId: '' }));
    }
  };

  // Handle clicking outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('#gym-search-container')) {
        setShowGymDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData.password.trim()) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters long';
    }

    if (!formData.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    if (!formData.gymId) {
      newErrors.gymId = 'Please select a gym';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const formErrors = validateForm();
    if (Object.keys(formErrors).length > 0) {
      setErrors(formErrors);
      return;
    }

    setIsLoading(true);
    setErrors({});

    try {
      await register({
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        password: formData.password,
        gymId: parseInt(formData.gymId)
      });
      // Redirect to dashboard after successful registration
      navigate('/dashboard');
    } catch (error) {
      setErrors({
        submit: error.message || 'Registration failed. Please try again.'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 via-purple-50 to-magenta-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-4xl font-bold text-slate-900 flex items-center justify-center gap-3">
            <span className="w-1 h-10 bg-gradient-to-b from-teal-500 to-purple-500 rounded"></span>
            Create your BoardRez account
          </h2>
          <p className="mt-3 text-center text-base font-medium text-slate-600">
            Join the climbing community
          </p>
        </div>
        
        <form className="mt-8 bg-white rounded-xl p-8 border-2 border-slate-900 shadow-retro space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label htmlFor="firstName" className="block text-sm font-bold text-slate-700 mb-2">
                  First name
                </label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  className={`appearance-none rounded-lg relative block w-full px-4 py-3 border-2 ${
                    errors.firstName ? 'border-coral-500 bg-coral-50' : 'border-slate-900'
                  } placeholder-slate-500 text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all sm:text-base`}
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                {errors.firstName && (
                  <p className="mt-2 text-sm font-medium text-coral-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.firstName}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-bold text-slate-700 mb-2">
                  Last name
                </label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  className={`appearance-none rounded-lg relative block w-full px-4 py-3 border-2 ${
                    errors.lastName ? 'border-coral-500 bg-coral-50' : 'border-slate-900'
                  } placeholder-slate-500 text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all sm:text-base`}
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  disabled={isLoading}
                />
                {errors.lastName && (
                  <p className="mt-2 text-sm font-medium text-coral-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            <div id="gym-search-container" className="relative">
              <label htmlFor="gymSearch" className="block text-sm font-bold text-slate-700 mb-2">
                Search for your gym
              </label>
              <input
                id="gymSearch"
                name="gymSearch"
                type="text"
                autoComplete="off"
                className={`appearance-none rounded-lg relative block w-full px-4 py-3 border-2 ${
                  errors.gymId ? 'border-coral-500 bg-coral-50' : 'border-slate-900'
                } placeholder-slate-500 text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all sm:text-base`}
                placeholder={loadingGyms ? 'Loading gyms...' : 'Start typing to search...'}
                value={gymSearchQuery}
                onChange={handleGymSearchChange}
                onFocus={() => setShowGymDropdown(true)}
                disabled={isLoading || loadingGyms}
              />
              {selectedGym && (
                <div className="mt-2 flex items-center gap-2 text-sm font-medium text-teal-700">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  Selected: {selectedGym.name}
                  {selectedGym.city && ` - ${selectedGym.city}, ${selectedGym.state}`}
                </div>
              )}

              {showGymDropdown && gymSearchQuery.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border-2 border-slate-900 rounded-lg shadow-retro max-h-60 overflow-y-auto">
                  {filteredGyms.length > 0 ? (
                    <ul>
                      {filteredGyms.slice(0, 50).map((gym) => (
                        <li
                          key={gym.id}
                          onClick={() => handleGymSelect(gym)}
                          className="px-4 py-3 hover:bg-teal-50 cursor-pointer border-b border-slate-200 last:border-b-0 transition-colors"
                        >
                          <div className="font-bold text-slate-900">{gym.name}</div>
                          {(gym.city || gym.address_line_1) && (
                            <div className="text-sm text-slate-600 mt-1">
                              {gym.address_line_1 && <div>{gym.address_line_1}{gym.address_line_2 ? `, ${gym.address_line_2}` : ''}</div>}
                              {gym.city && <div>{gym.city}, {gym.state} {gym.zip_code}</div>}
                            </div>
                          )}
                        </li>
                      ))}
                      {filteredGyms.length > 50 && (
                        <li className="px-4 py-2 text-sm text-slate-500 italic bg-slate-50">
                          Showing first 50 results. Keep typing to narrow down...
                        </li>
                      )}
                    </ul>
                  ) : (
                    <div className="px-4 py-6 text-center text-slate-600">
                      <svg className="w-12 h-12 mx-auto mb-2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      <p className="font-medium">No gyms found</p>
                      <p className="text-sm mt-1">Try a different search term</p>
                    </div>
                  )}
                </div>
              )}

              {errors.gymId && (
                <p className="mt-2 text-sm font-medium text-coral-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.gymId}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-bold text-slate-700 mb-2">
                Email address
              </label>
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                className={`appearance-none rounded-lg relative block w-full px-4 py-3 border-2 ${
                  errors.email ? 'border-coral-500 bg-coral-50' : 'border-slate-900'
                } placeholder-slate-500 text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all sm:text-base`}
                placeholder="Enter your email"
                value={formData.email}
                onChange={handleChange}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="mt-2 text-sm font-medium text-coral-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.email}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-slate-700 mb-2">
                Password
              </label>
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                className={`appearance-none rounded-lg relative block w-full px-4 py-3 border-2 ${
                  errors.password ? 'border-coral-500 bg-coral-50' : 'border-slate-900'
                } placeholder-slate-500 text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all sm:text-base`}
                placeholder="Create a password"
                value={formData.password}
                onChange={handleChange}
                disabled={isLoading}
              />
              {errors.password && (
                <p className="mt-2 text-sm font-medium text-coral-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.password}
                </p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-bold text-slate-700 mb-2">
                Confirm password
              </label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                className={`appearance-none rounded-lg relative block w-full px-4 py-3 border-2 ${
                  errors.confirmPassword ? 'border-coral-500 bg-coral-50' : 'border-slate-900'
                } placeholder-slate-500 text-slate-900 font-medium focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all sm:text-base`}
                placeholder="Confirm your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                disabled={isLoading}
              />
              {errors.confirmPassword && (
                <p className="mt-2 text-sm font-medium text-coral-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                  {errors.confirmPassword}
                </p>
              )}
            </div>
          </div>

          {errors.submit && (
            <div className="rounded-lg bg-coral-50 border-2 border-coral-500 p-4">
              <div className="flex gap-3">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-coral-600" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h3 className="text-sm font-bold text-coral-800">
                    Registration Error
                  </h3>
                  <p className="text-sm font-medium text-coral-700 mt-1">
                    {errors.submit}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div>
            <Button
              type="submit"
              loading={isLoading}
              disabled={isLoading}
              className="w-full"
              size="large"
            >
              Create account
            </Button>
          </div>

          <div className="text-center pt-2">
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="text-sm font-bold text-teal-600 hover:text-teal-700 transition-colors touch-manipulation"
              disabled={isLoading}
            >
              Already have an account? Sign in
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Register;